/**
 * LeaguesDirectoryPage - Public page showcasing all leagues
 * Displays a grid of league cards with filtering options
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Users, Calendar, Search, Loader2, ChevronRight } from "lucide-react";
import axios from "axios";
import { MetaTags } from "./MetaTags";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const LeaguesDirectoryPage = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sportFilter, setSportFilter] = useState("all");

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/leagues`);
        // Filter only active leagues
        const activeLeagues = res.data.filter(league => league.is_active !== false);
        setLeagues(activeLeagues);
      } catch (err) {
        console.error("Failed to load leagues:", err);
        setError("Failed to load leagues");
      } finally {
        setLoading(false);
      }
    };
    fetchLeagues();
  }, []);

  // Get unique sports for filter
  const sports = [...new Set(leagues.map(l => l.sport).filter(Boolean))];

  // Filter leagues by search and sport
  const filteredLeagues = leagues.filter(league => {
    const matchesSearch = !searchQuery || 
      league.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      league.abbreviation?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSport = sportFilter === "all" || league.sport === sportFilter;
    return matchesSearch && matchesSport;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4">
        <Trophy className="w-16 h-16 text-zinc-700 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Leagues</h1>
        <p className="text-zinc-400 mb-6">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="text-orange-500 hover:text-orange-400"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="leagues-directory-page">
      <MetaTags
        title="Leagues Directory | Antler Sports Network"
        description="Browse all sports leagues on Antler Sports Network. Find standings, schedules, and playoffs for your favorite leagues."
      />

      {/* Hero Section */}
      <div className="relative py-16 border-b border-zinc-800 bg-gradient-to-b from-orange-500/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase text-white tracking-wider mb-4">
              Leagues
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Explore sports leagues, view standings, schedules, and playoff brackets
            </p>
          </div>

          {/* Search & Filter Bar */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                placeholder="Search leagues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500"
                data-testid="leagues-search-input"
              />
            </div>
            
            {sports.length > 0 && (
              <select
                value={sportFilter}
                onChange={(e) => setSportFilter(e.target.value)}
                className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-orange-500"
                data-testid="leagues-sport-filter"
              >
                <option value="all">All Sports</option>
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Leagues Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {filteredLeagues.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-xl text-zinc-400">
              {searchQuery || sportFilter !== "all" 
                ? "No leagues match your search" 
                : "No leagues available yet"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLeagues.map((league, idx) => (
              <motion.div
                key={league.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <LeagueCard league={league} />
              </motion.div>
            ))}
          </div>
        )}

        {/* Stats Summary */}
        {filteredLeagues.length > 0 && (
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <p className="text-center text-zinc-500">
              Showing {filteredLeagues.length} of {leagues.length} league{leagues.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// League Card Component
const LeagueCard = ({ league }) => {
  const slug = league.slug || league.name.toLowerCase().replace(/\s+/g, '-');
  
  return (
    <Link 
      to={`/league/${slug}`}
      className="block group"
      data-testid={`league-card-${league.id}`}
    >
      <div 
        className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-all hover:shadow-lg hover:shadow-orange-500/5"
      >
        {/* Card Header with Logo */}
        <div 
          className="h-32 relative flex items-center justify-center"
          style={{ 
            background: `linear-gradient(to bottom, ${league.color || '#f97316'}30, transparent)` 
          }}
        >
          {league.logo_url ? (
            <img 
              src={league.logo_url} 
              alt={league.name} 
              className="w-20 h-20 object-contain group-hover:scale-110 transition-transform"
            />
          ) : (
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: league.color || '#f97316' }}
            >
              <Trophy className="w-10 h-10 text-white" />
            </div>
          )}
        </div>

        {/* Card Content */}
        <div className="p-5">
          <h3 className="font-heading text-xl uppercase text-white tracking-wide group-hover:text-orange-400 transition-colors">
            {league.name}
          </h3>
          
          {league.description && (
            <p className="text-zinc-500 text-sm mt-2 line-clamp-2">
              {league.description}
            </p>
          )}

          <div className="mt-4 flex items-center gap-4 text-sm text-zinc-400">
            {league.sport && (
              <span className="flex items-center gap-1">
                <Trophy className="w-4 h-4" />
                {league.sport}
              </span>
            )}
            {league.teams?.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {league.teams.length} Teams
              </span>
            )}
            {league.season && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {league.season}
              </span>
            )}
          </div>

          {/* View Button */}
          <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
            <span className="text-sm text-orange-500 font-medium group-hover:text-orange-400">
              View League
            </span>
            <ChevronRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default LeaguesDirectoryPage;
